import { classifyEnvironmentId } from "./environments/environmentIdClassification";
/**
 * A checkout as far as the Checkout switcher is concerned.
 *
 * Declared with plain strings rather than `Pick<Project, ...>` so this stays a
 * function of the shape it actually reads. Branded ids are assignable to
 * `string`, so real projects still fit, while fixtures do not have to mint
 * brands to describe two paths and an id.
 */
interface Checkout {
  readonly environmentId: string;
  readonly id: string;
  readonly cwd: string;
}

/** Separates the connection from the path, matching t3code's switcher. */
const LABEL_SEPARATOR = " · ";

export interface CheckoutLabelInput {
  readonly environmentLabel: string | null | undefined;
  readonly workspaceRoot: string;
}

/**
 * How one checkout reads in the switcher.
 *
 * Mirrors t3code: `{environmentLabel ?? "This machine"} · {workspaceRoot}`. The
 * path belongs in the LABEL rather than in a subtitle because two connections
 * frequently hold the same repo, and the connection name alone does not tell
 * you which working copy you are about to change.
 */
export function describeCheckoutLabel(input: CheckoutLabelInput): string {
  return `${resolveConnectionLabel(input.environmentLabel)}${LABEL_SEPARATOR}${input.workspaceRoot}`;
}

/**
 * A blank label reads to a user exactly like an absent one, so it resolves the
 * same way rather than rendering a gap before the separator.
 */
function resolveConnectionLabel(environmentLabel: string | null | undefined): string {
  const label = environmentLabel?.trim();
  return label ? label : "This machine";
}

export interface CheckoutOption {
  /** Addresses ONE checkout, stable across reloads. */
  readonly value: string;
  /** Connection + FULL path, for the dropdown, which has room for it. */
  readonly label: string;
  /** Connection + the shortest path suffix unique in this group, for the trigger. */
  readonly shortLabel: string;
  readonly environmentId: string;
  readonly projectId: string;
  readonly workspaceRoot: string;
}

/**
 * Every checkout in the group, as switcher options.
 *
 * Keyed by connection AND project: a cwd is not unique (two connections often
 * hold the same path) and a project id is unique only within its connection, so
 * either alone would let a selection silently address the wrong working copy.
 */
export function resolveCheckoutOptions(
  members: readonly Checkout[],
  environmentLabelById: Readonly<Record<string, string | null | undefined>>,
): readonly CheckoutOption[] {
  // Disambiguated WITHIN each connection, not across all of them: the
  // connection name already separates a laptop's checkout from a sprite's, so
  // growing both paths to tell them apart would add noise that distinguishes
  // nothing. Only siblings on the same connection can collide.
  const shortPathByMember = new Map<string, string>();
  const byConnection = new Map<string, { readonly key: string; readonly cwd: string }[]>();
  for (const member of members) {
    const key = `${member.environmentId} ${member.id}`;
    const siblings = byConnection.get(member.environmentId) ?? [];
    siblings.push({ key, cwd: member.cwd });
    byConnection.set(member.environmentId, siblings);
  }
  for (const siblings of byConnection.values()) {
    const shortPaths = describeCheckoutShortLabels(siblings.map((sibling) => sibling.cwd));
    for (const [index, sibling] of siblings.entries()) {
      shortPathByMember.set(sibling.key, shortPaths[index] ?? sibling.cwd);
    }
  }

  // With every checkout on ONE connection the name prefixes all of them
  // identically, so it distinguishes nothing and only eats the trigger's 256px.
  // Dropped there for the same reason the path is not over-disambiguated above.
  const spansConnections = byConnection.size > 1;

  return members.map((member) => {
    const connection = resolveConnectionLabel(environmentLabelById[member.environmentId]);
    const key = `${member.environmentId} ${member.id}`;
    return {
      value: key,
      label: describeCheckoutLabel({
        environmentLabel: environmentLabelById[member.environmentId],
        workspaceRoot: member.cwd,
      }),
      shortLabel: spansConnections
        ? `${connection}${LABEL_SEPARATOR}${shortPathByMember.get(key) ?? member.cwd}`
        : (shortPathByMember.get(key) ?? member.cwd),
      environmentId: member.environmentId,
      projectId: member.id,
      workspaceRoot: member.cwd,
    };
  });
}

/**
 * The shortest tail of each path that is unique among them.
 *
 * A path's identifying part is its END, but the switcher trigger ellipsises
 * from the right — so with twenty worktrees of one repo it cut inside the
 * `This machine · /Users/<me>/` prefix they all share and rendered twenty
 * identical labels. Measured in the running app: 351px of text in a 212px box.
 *
 * Only the paths that actually collide pay for disambiguation; a checkout whose
 * last segment is already unique keeps just that segment.
 */
export function describeCheckoutShortLabels(paths: readonly string[]): readonly string[] {
  const segmentsOf = paths.map((path) => path.split("/").filter(Boolean));
  const depth = segmentsOf.reduce((longest, segments) => Math.max(longest, segments.length), 1);
  const labels = segmentsOf.map((segments) => suffixOf(segments, 1));

  // Grow leftwards a segment at a time, but only where a label is still shared.
  // Bounded by the deepest path, so identical paths terminate rather than
  // looping for a suffix that cannot exist.
  for (let take = 2; take <= depth; take += 1) {
    const counts = new Map<string, number>();
    for (const label of labels) counts.set(label, (counts.get(label) ?? 0) + 1);
    if ([...counts.values()].every((count) => count === 1)) break;

    for (const [index, segments] of segmentsOf.entries()) {
      const current = labels[index];
      if (current !== undefined && (counts.get(current) ?? 0) > 1) {
        labels[index] = suffixOf(segments, take);
      }
    }
  }

  return labels;
}

/** The last `take` segments, never empty — a root-level path keeps its name. */
function suffixOf(segments: readonly string[], take: number): string {
  const tail = segments.slice(Math.max(0, segments.length - take));
  return tail.length > 0 ? tail.join("/") : (segments.join("/") ?? "");
}

/**
 * The checkout the section should show, or `null` when there are none.
 *
 * Falls back to the first rather than to nothing, because a selection can name
 * a checkout that has since been removed — and an empty Checkout section for a
 * group that HAS checkouts asserts something false.
 */
export function resolveSelectedCheckout(
  options: readonly CheckoutOption[],
  selectedValue: string,
): CheckoutOption | null {
  return options.find((option) => option.value === selectedValue) ?? options[0] ?? null;
}

/** Thread count for the checkout bar. Singular only at exactly one. */
export function describeThreadCount(count: number): string {
  return count === 1 ? "1 thread" : `${count} threads`;
}

/** A thread as far as counting per checkout is concerned. */
interface CountableThread {
  readonly environmentId: string;
  readonly projectId: string;
}

/**
 * How many threads live on one checkout.
 *
 * Matched on connection AND project: a project id is unique only within its
 * connection, so counting by id alone would fold a sprite's threads into the
 * laptop's count for any two checkouts that happen to share one.
 */
export function countThreadsForCheckout(
  threads: readonly CountableThread[],
  checkout: { readonly environmentId: string; readonly projectId: string } | null,
): number {
  if (checkout === null) return 0;
  return threads.filter(
    (thread) =>
      thread.environmentId === checkout.environmentId && thread.projectId === checkout.projectId,
  ).length;
}

/** The slice of an environment's state that indexes threads by project. */
interface ThreadIndexedEnvironment {
  readonly threadIdsByProjectId?: Readonly<Record<string, readonly unknown[]>>;
}

/**
 * How many threads the store has already indexed for one checkout.
 *
 * Reads the existing index rather than building a thread array, because a
 * `useStore` selector that constructs an array returns a NEW reference every
 * call and re-renders forever — React error #185. That is how the group
 * settings page crashed in production, so this shape is load-bearing, not a
 * micro-optimisation.
 */
export function countIndexedThreadsForCheckout(
  environmentStateById: Readonly<Record<string, ThreadIndexedEnvironment | undefined>>,
  checkout: { readonly environmentId: string; readonly projectId: string } | null,
): number {
  if (checkout === null) return 0;
  const byProject = environmentStateById[checkout.environmentId]?.threadIdsByProjectId;
  return byProject?.[checkout.projectId]?.length ?? 0;
}

/**
 * Whether the settings page may offer to remove the checkout it is showing.
 *
 * `last-checkout` is not a cosmetic gate. Removing the only checkout deletes
 * the group's last project row, so the page you are standing on stops
 * resolving and any name or model you set has nothing left to be stored
 * against. Removing a whole project is the sidebar's job, because that also
 * tears down the runtime and the CMMD link.
 *
 * `none` and `last-checkout` are kept apart rather than collapsed into one
 * falsy value: they need different copy, and a single boolean is how "there is
 * nothing here" ends up explained as "you cannot remove your last checkout".
 */
export type CheckoutRemoval =
  | { readonly kind: "none" }
  | { readonly kind: "last-checkout" }
  | {
      readonly kind: "removable";
      readonly environmentId: string;
      readonly projectId: string;
      readonly label: string;
      /**
       * True when the checkout's environment id encodes a legacy runtime id.
       * No reader consumes this today: `projectCheckoutRemovalRunner.ts` never
       * calls `destroyRuntime`, and the one settings row that renders removal
       * copy uses a fixed string regardless of this value. Candidate for
       * deletion in a follow-up; kept as-is here since removing it is outside
       * this ticket's scope.
       */
      readonly destroysRuntime: boolean;
    };

export function resolveCheckoutRemoval(
  checkouts: readonly CheckoutOption[],
  selectedValue: string,
): CheckoutRemoval {
  if (checkouts.length === 0) return { kind: "none" };
  if (checkouts.length === 1) return { kind: "last-checkout" };

  // Resolved through the same helper the rest of the page uses, so an
  // unrecognised selection removes the checkout the body is describing rather
  // than a different one.
  const selected = resolveSelectedCheckout(checkouts, selectedValue);
  if (selected === null) return { kind: "none" };

  return {
    kind: "removable",
    environmentId: selected.environmentId,
    projectId: selected.projectId,
    label: selected.label,
    destroysRuntime: classifyEnvironmentId(selected.environmentId).legacyRuntimeId !== null,
  };
}

/**
 * Whether the settings page can write anything, and if not, why.
 *
 * Three outcomes rather than a boolean. "Nothing is here" and "it is here but
 * nothing on this machine is connected to it" need different copy, and
 * collapsing them is how a cloud project came to be told to "open this
 * repository from a connection" while the reader was standing on one.
 *
 * The Name field was already disabled for the empty case; the Model row was
 * not, so it accepted a change that `planGroupMetaUpdate` then planned zero
 * updates for and discarded in silence.
 */
export type GroupWritability =
  | { readonly kind: "writable" }
  | { readonly kind: "no-checkout" }
  | { readonly kind: "not-connected"; readonly label: string };

export function resolveGroupWritability(
  checkouts: readonly { readonly environmentId: string; readonly label: string }[],
  isEnvironmentLoaded: (environmentId: string) => boolean,
): GroupWritability {
  const first = checkouts[0];
  if (first === undefined) return { kind: "no-checkout" };
  // One connected member is enough: the fan-out writes to every member it can
  // reach, and a partially reachable group is still worth editing.
  if (checkouts.some((checkout) => isEnvironmentLoaded(checkout.environmentId))) {
    return { kind: "writable" };
  }
  return { kind: "not-connected", label: first.label };
}
